import { FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { IconCheck, IconCircleCheck, IconLogout, IconPhotoUp, IconUpload, IconX } from "@tabler/icons-react"
import { CHAPTER_REGISTRY } from "../lib/chapters"
import { getPublicSiteOrigin } from "../lib/adminHost"
import { useChapterIntakeAuth } from "../hooks/useChapterIntakeAuth"
import apiClient from "../lib/api"

type AssetSlot = {
  id: string
  type: string
  label: string
  description: string
  required?: boolean
  multiple?: boolean
}

const ASSET_SLOTS: AssetSlot[] = [
  {
    id: "hero",
    type: "hero",
    label: "Hero banner",
    description: "Wide landscape photo for the top of your chapter page. Min. 1600px wide.",
    required: true,
  },
  {
    id: "sunday-poster",
    type: "sunday-poster",
    label: "Sunday service poster",
    description: "Image for the Sunday service card. Landscape, min. 1200px wide.",
    required: true,
  },
  {
    id: "midweek-poster",
    type: "midweek-poster",
    label: "Midweek service poster",
    description: "Optional — image for midweek or online service card.",
  },
  {
    id: "gallery",
    type: "gallery",
    label: "Gallery photos",
    description: "Photos from services or community life. Select multiple files.",
    multiple: true,
  },
  {
    id: "event",
    type: "event",
    label: "Event images",
    description: "Optional — images for events you listed on the form.",
    multiple: true,
  },
  {
    id: "news",
    type: "news",
    label: "News images",
    description: "Optional — images for news items from the form.",
    multiple: true,
  },
  {
    id: "birthday",
    type: "birthday",
    label: "Birthday photos",
    description: "Optional — photos for birthday shoutouts. Confirm you have permission.",
    multiple: true,
  },
]

type SlotState = {
  files: File[]
  names: string[]
  previews: string[]
}

const defaultSlotState = (): SlotState => ({ files: [], names: [], previews: [] })

const formatExpiry = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })

const ChapterIntakeUploadPage = () => {
  const { session, loading, logout } = useChapterIntakeAuth()
  const chapterOptions = useMemo(() => CHAPTER_REGISTRY.map((c) => ({ slug: c.slug, name: c.name })), [])

  const [slots, setSlots] = useState<Record<string, SlotState>>(() =>
    Object.fromEntries(ASSET_SLOTS.map((s) => [s.id, defaultSlotState()]))
  )
  const [uploading, setUploading] = useState(false)
  const [uploadLabel, setUploadLabel] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [uploadCount, setUploadCount] = useState(0)
  const slotsRef = useRef(slots)
  slotsRef.current = slots

  const chapterSlug = session?.chapterSlug ?? ""
  const contactEmail = session?.contactEmail ?? ""
  const chapterName = chapterOptions.find((c) => c.slug === chapterSlug)?.name

  useEffect(() => {
    return () => {
      Object.values(slotsRef.current).forEach((slot) => {
        slot.previews.forEach((url) => URL.revokeObjectURL(url))
      })
    }
  }, [])

  const requiredComplete = Boolean(slots.hero?.files.length && slots["sunday-poster"]?.files.length)
  const optionalCount = ASSET_SLOTS.filter((s) => !s.required).reduce(
    (n, s) => n + (slots[s.id]?.files.length ?? 0),
    0
  )

  const revokePreviews = (slotId: string) => {
    const existing = slots[slotId]?.previews ?? []
    existing.forEach((url) => URL.revokeObjectURL(url))
  }

  const setSlotFiles = (slotId: string, files: FileList | null) => {
    if (!files?.length) return
    revokePreviews(slotId)
    const list = Array.from(files)
    setSlots((prev) => ({
      ...prev,
      [slotId]: {
        files: list,
        names: list.map((f) => f.name.replace(/\.[^/.]+$/, "")),
        previews: list.map((f) => URL.createObjectURL(f)),
      },
    }))
  }

  const clearSlot = (slotId: string) => {
    revokePreviews(slotId)
    setSlots((prev) => ({
      ...prev,
      [slotId]: defaultSlotState(),
    }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!chapterSlug || !session) {
      setError("Upload session expired. Sign in again with your upload link.")
      return
    }

    const hero = slots.hero?.files ?? []
    const sunday = slots["sunday-poster"]?.files ?? []
    if (!hero.length || !sunday.length) {
      setError("Hero banner and Sunday service poster are required.")
      return
    }

    const batches: Array<{ slot: AssetSlot; payload: Array<{ file: File; type: string; name: string }> }> = []

    for (const slot of ASSET_SLOTS) {
      const state = slots[slot.id]
      if (!state?.files.length) continue
      const payload = state.files.map((file, i) => ({
        file,
        type: slot.type,
        name: state.names[i]?.trim() || file.name.replace(/\.[^/.]+$/, ""),
      }))
      batches.push({ slot, payload })
    }

    setUploading(true)
    let totalUploaded = 0

    try {
      for (const { slot, payload } of batches) {
        setUploadLabel(slot.label)
        const result = await apiClient.uploadChapterIntakeAssets(payload)
        totalUploaded += result.uploads.length
      }
      setUploadCount(totalUploaded)
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setUploading(false)
      setUploadLabel(null)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f4f6fb]">
        <span className="loading loading-spinner loading-lg text-[#00205c]" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f4f6fb]">
      <header className="border-b border-[#e2e8f0] bg-white px-6 py-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/dominion-city-logo.png" alt="" className="h-9 w-9 object-contain" />
            <div>
              <p className="font-outfit text-sm font-semibold uppercase tracking-wide text-[#00205c]">
                Dominion City
              </p>
              <p className="font-dm-sans text-xs uppercase tracking-[0.16em] text-[#9e7e43]">Chapter intake</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-1.5 font-dm-sans text-sm text-[#5a5a5a] transition hover:text-[#00205c]"
            >
              <IconLogout size={16} />
              Sign out
            </button>
            <a
              href={getPublicSiteOrigin()}
              className="font-dm-sans text-sm text-[#5a5a5a] transition hover:text-[#00205c]"
            >
              ← Back to website
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 md:px-8 md:py-14">
        <div className="flex items-center gap-3 font-dm-sans text-sm">
          <span className="flex items-center gap-1.5 text-[#787878]">
            <IconCircleCheck size={18} className="text-emerald-600" stroke={1.5} />
            Step 1 — Form submitted
          </span>
          <span className="text-[#c4c4c4]">→</span>
          <span className="font-semibold text-[#00205c]">Step 2 — Upload images</span>
        </div>

        <h1 className="mt-4 font-outfit text-3xl font-semibold tracking-tight text-[#1f1f1f] md:text-4xl">
          {chapterName ? `Upload images for ${chapterName}` : "Upload chapter images"}
        </h1>
        <p className="mt-3 font-dm-sans text-base leading-7 text-[#5a5a5a]">
          You&apos;re signed in with a temporary upload pass. Images are linked to your form response automatically.
        </p>

        {session && (
          <div className="mt-4 rounded-xl border border-[#e2e8f0] bg-white px-4 py-3 font-dm-sans text-sm text-[#5a5a5a]">
            <p>
              <span className="font-medium text-[#1f1f1f]">Chapter:</span> {chapterName ?? chapterSlug}
            </p>
            <p className="mt-1">
              <span className="font-medium text-[#1f1f1f]">Form email:</span> {contactEmail}
            </p>
            <p className="mt-1">
              <span className="font-medium text-[#1f1f1f]">Access expires:</span> {formatExpiry(session.expiresAt)} ·{" "}
              {session.maxUploads - session.uploadCount} uploads remaining
            </p>
          </div>
        )}

        {done ? (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-10 text-center">
            <IconCheck className="mx-auto text-emerald-600" size={44} stroke={1.5} />
            <h2 className="mt-4 font-outfit text-2xl font-semibold text-emerald-900">You&apos;re all set</h2>
            <p className="mt-2 font-dm-sans text-sm leading-6 text-emerald-800">
              {uploadCount} file{uploadCount === 1 ? "" : "s"} saved for {chapterName ?? chapterSlug}. We&apos;ll review
              your form answers and images together — no further action needed.
            </p>
            <p className="mt-4 font-dm-sans text-xs text-emerald-700">
              You can sign in again with the same upload link or passkey until access expires.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            {ASSET_SLOTS.map((slot) => {
              const state = slots[slot.id]
              const hasFiles = Boolean(state?.files.length)
              return (
                <section
                  key={slot.id}
                  className={`rounded-2xl border bg-white p-6 shadow-sm transition ${
                    hasFiles ? "border-emerald-200" : "border-[#e2e8f0]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                        hasFiles ? "bg-emerald-50 text-emerald-700" : "bg-[#eef2f9] text-[#00205c]"
                      }`}
                    >
                      {hasFiles ? <IconCheck size={20} stroke={2} /> : <IconPhotoUp size={20} stroke={1.5} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-outfit text-lg font-semibold text-[#1f1f1f]">
                          {slot.label}
                          {slot.required && <span className="text-[#00205c]"> *</span>}
                        </h2>
                        {hasFiles && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-dm-sans text-xs font-medium text-emerald-800">
                            {state.files.length} selected
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-dm-sans text-sm leading-6 text-[#5a5a5a]">{slot.description}</p>

                      {hasFiles && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          {state.previews.map((preview, i) => (
                            <div
                              key={`${slot.id}-${i}`}
                              className="relative h-20 w-28 overflow-hidden rounded-lg border border-[#e2e8f0] bg-[#f4f6fb]"
                            >
                              <img src={preview} alt="" className="h-full w-full object-cover" />
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => clearSlot(slot.id)}
                            className="flex h-20 items-center gap-1 rounded-lg border border-dashed border-[#d8dee9] px-3 font-dm-sans text-xs text-[#787878] transition hover:border-red-300 hover:text-red-600"
                          >
                            <IconX size={14} />
                            Clear
                          </button>
                        </div>
                      )}

                      <label className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#d8dee9] bg-[#fafbfc] px-4 py-7 transition hover:border-[#00205c]/30 hover:bg-[#eef2f9]">
                        <IconUpload size={22} className="text-[#00205c]" stroke={1.5} />
                        <span className="mt-2 font-dm-sans text-sm font-medium text-[#00205c]">
                          {hasFiles ? "Add or replace image(s)" : "Choose image(s)"}
                        </span>
                        <span className="mt-1 font-dm-sans text-xs text-[#787878]">
                          JPEG, PNG, GIF, WebP — max 10MB each
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          multiple={slot.multiple}
                          className="sr-only"
                          onChange={(e) => setSlotFiles(slot.id, e.target.files)}
                        />
                      </label>
                    </div>
                  </div>
                </section>
              )
            })}

            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 font-dm-sans text-sm text-red-800">
                {error}
              </p>
            )}

            <div className="sticky bottom-0 -mx-4 border-t border-[#e2e8f0] bg-[#f4f6fb]/95 px-4 py-4 backdrop-blur md:-mx-8 md:px-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-dm-sans text-sm text-[#5a5a5a]">
                  {requiredComplete ? (
                    <span className="text-emerald-700">
                      Required images ready
                      {optionalCount > 0 ? ` · ${optionalCount} optional file${optionalCount === 1 ? "" : "s"}` : ""}
                    </span>
                  ) : (
                    "Hero banner and Sunday poster are required"
                  )}
                </p>
                <button
                  type="submit"
                  disabled={uploading || !requiredComplete}
                  className="rounded-xl bg-[#00205c] px-8 py-3.5 font-dm-sans text-sm font-semibold text-white transition hover:bg-[#001a4d] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {uploading ? (uploadLabel ? `Uploading ${uploadLabel}…` : "Uploading…") : "Submit images"}
                </button>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  )
}

export default ChapterIntakeUploadPage
